/**
 * Monday.com Lead Domain Service
 *
 * Lead-specific operations extracted from the monolithic MondayService.
 * All functions take a MondayCore instance as their first parameter.
 */

import {
  MondayCore,
  DEFAULT_LEAD_COLUMN_MAPPING,
  transformItemToLead,
  getAvailableColumnIds,
  hubItemToMondayItem,
} from './mondayCore';
import type {
  HubBoardItem,
  HubBoardItemsPage,
  Lead,
} from './mondayCore';
import { mondayClient } from './client';
import { logger } from '@/utils/logger';

// ============================================================================
// Lead Operations
// ============================================================================

/**
 * Get all leads from the CRM board
 * Uses pagination to handle large boards efficiently
 * Auto-detects column IDs from board structure
 * Filters out unqualified leads automatically
 */
export async function getLeads(_core: MondayCore, _pageSize?: number): Promise<Lead[]> {
  const leads = await mondayClient.get<Lead[]>('/hub/leads', {
    cache: true,
    cacheTTL: 60 * 60 * 1000,
  });
  logger.log(`[Hub] Total leads fetched: ${leads.length}`);
  return leads;
}


/**
 * Check enrichment status from linked accounts in Monday (DEPRECATED - now done in getLeads)
 * Kept for backwards compatibility but returns empty map
 */
export async function getLinkedAccountEnrichmentStatus(
  _core: MondayCore,
  _leadIds: string[]
): Promise<Map<string, { enriched: boolean; enrichmentDate?: string }>> {
  // This is now handled efficiently inside getLeads()
  logger.log('[Monday] getLinkedAccountEnrichmentStatus is deprecated - enrichment is now checked in getLeads');
  return new Map();
}

/**
 * Get a single lead by ID
 */
export async function getLead(core: MondayCore, itemId: string): Promise<Lead | null> {
  const config = core.getConfig();
  const columnMapping = config.columnMappings?.leads ?? DEFAULT_LEAD_COLUMN_MAPPING;

  const hubItem = await mondayClient.get<HubBoardItem>(`/hub/item/${itemId}`);
  if (!hubItem || !('column_values' in hubItem)) return null;
  const item = hubItemToMondayItem(hubItem);
  return transformItemToLead(item, columnMapping);
}

/**
 * Check if a lead exists by email (for deduplication)
 * EFFICIENT: Uses column value search instead of fetching all items
 */
export async function findLeadByEmail(
  core: MondayCore,
  email: string
): Promise<{ id: string; name: string } | null> {
  const config = core.getConfig();
  const columnMapping = config.columnMappings?.leads ?? DEFAULT_LEAD_COLUMN_MAPPING;
  const emailColumnId = columnMapping['contactEmail'] ?? 'email';

  const page = await mondayClient.get<HubBoardItemsPage>(
    `/hub/items/leads/${encodeURIComponent(emailColumnId)}?column_value=${encodeURIComponent(email)}&limit=1`,
  );
  if (!page?.data) return null;
  return page.data[0] ? { id: page.data[0].id, name: page.data[0].name } : null;
}

/**
 * Create a new lead
 * Returns the created item ID
 * SAFE: Auto-detects available columns and skips non-existent ones
 * @param lead - Lead data
 * @param options - Options for creating the lead
 * @param options.groupId - Specific group ID to use
 * @param options.listName - List name to create/find a group for (takes precedence over groupId)
 */
export async function createLead(
  core: MondayCore,
  lead: Omit<Lead, 'id' | 'createdAt' | 'lastActivity'>,
  options?: { groupId?: string; listName?: string }
): Promise<string> {
  const config = core.getConfig();
  const cm = config.columnMappings?.leads ?? DEFAULT_LEAD_COLUMN_MAPPING;

  // If listName is provided, find or create a group for it
  let targetGroupId: string | undefined = options?.groupId;
  if (options?.listName) {
    logger.log(`[Monday] Finding or creating group for list: ${options.listName}`);
    targetGroupId = await core.findOrCreateGroup(config.leadsBoardId, options.listName);
  }

  // Get available columns and groups from the board to avoid "column doesn't exist" errors
  let availableColumns: Set<string> | null = null;
  let defaultGroupId: string | null = null;
  let board: Awaited<ReturnType<typeof core.getBoard>> | null = null;
  try {
    board = await core.getBoard(config.leadsBoardId);
    if (board?.columns) {
      availableColumns = getAvailableColumnIds(board.columns);
      logger.log('[Monday] Available lead columns for create:', Array.from(availableColumns));
    }
    if (board?.groups && board.groups.length > 0) {
      const firstGroup = board.groups[0];
      if (firstGroup) {
        defaultGroupId = firstGroup.id;
        logger.log('[Monday] Default group for leads:', defaultGroupId, `(${firstGroup.title})`);
      }
    }
  } catch (err) {
    logger.simpleWarn('[Monday] Could not fetch board columns/groups, will attempt all updates:', err);
  }

  // Helper to safely get column ID
  const col = (key: string, fallback: string) => cm[key] ?? fallback;

  // Helper to check if column exists on board
  const columnExists = (columnId: string): boolean => {
    if (!availableColumns) return true; // If we couldn't fetch columns, try anyway
    return availableColumns.has(columnId);
  };

  // Build column values JSON
  const columnValues: Record<string, unknown> = {};
  const skippedColumns: string[] = [];

  // Helper to add column value if column exists
  const addIfExists = (key: string, fallback: string, value: unknown) => {
    const colId = col(key, fallback);
    if (columnExists(colId)) {
      columnValues[colId] = value;
    } else {
      skippedColumns.push(`${key} (${colId})`);
    }
  };

  if (lead.contactName) {
    addIfExists('contactName', 'contact_name', lead.contactName);
  }
  if (lead.contactTitle) {
    addIfExists('contactTitle', 'contact_title', lead.contactTitle);
  }
  if (lead.contactEmail) {
    addIfExists('contactEmail', 'email', {
      email: lead.contactEmail,
      text: lead.contactEmail,
    });
  }
  // Skip phone field - Monday's API is too strict about format
  // Phone can be manually added in Monday.com after import
  if (lead.contactPhone) {
    logger.log(`[Monday] Skipping phone field for lead: ${lead.contactPhone}`);
  }
  if (lead.stage) {
    addIfExists('stage', 'status', { label: lead.stage });
  }
  if (lead.value) {
    addIfExists('value', 'numbers', String(lead.value));
  }
  if (lead.source) {
    addIfExists('source', 'source', lead.source);
  }
  if (lead.notes) {
    addIfExists('notes', 'long_text', { text: lead.notes });
  }
  if (lead.nextAction) {
    addIfExists('nextAction', 'next_action', lead.nextAction);
  }
  if (lead.nextActionDate) {
    addIfExists('nextActionDate', 'date', { date: lead.nextActionDate });
  }
  // Set enriched status and enrichment date by finding columns by title
  if (lead.apolloEnriched) {
    if (board?.columns) {
      // Find Enriched column by title
      const enrichedColumn = board.columns.find(
        (c) => c.title.toLowerCase().startsWith('enriched') || c.id === 'enriched' || (c.id.startsWith('boolean_') && c.title.toLowerCase().includes('enriched'))
      );
      if (enrichedColumn) {
        columnValues[enrichedColumn.id] = { checked: 'true' };
        logger.log(`[Monday] Setting Enriched column on leads: ${enrichedColumn.id}`);
      } else {
        logger.log('[Monday] Enriched column not found on leads board');
      }

      // Find Enrichment Date column by title
      const enrichmentDateColumn = board.columns.find(
        (c) => c.title.toLowerCase() === 'enrichment date' || c.id === 'enrichment_date'
      );
      if (enrichmentDateColumn) {
        columnValues[enrichmentDateColumn.id] = { date: new Date().toISOString().split('T')[0] };
        logger.log(`[Monday] Setting Enrichment Date column on leads: ${enrichmentDateColumn.id}`);
      } else {
        logger.log('[Monday] Enrichment Date column not found on leads board');
      }
    }
  }

  if (skippedColumns.length > 0) {
    logger.log('[Monday] Skipped non-existent columns on create:', skippedColumns.join(', '));
  }

  // Determine the group to use (targetGroupId may already be set from listName)
  const finalGroupId = targetGroupId ?? defaultGroupId;
  if (!finalGroupId) {
    throw new Error('No group ID provided and could not determine a default group from the board');
  }

  const created = await mondayClient.post<{ id: string; name: string }>(
    '/hub/items',
    {
      boardId: config.leadsBoardId,
      groupId: finalGroupId,
      itemName: lead.company,
      columnValues,
    },
  );

  // Invalidate leads cache
  core.invalidateLeadsCache();
  mondayClient.invalidateCache('/hub/leads');

  return created.id;
}

/**
 * Update lead fields
 * EFFICIENT: Uses change_multiple_column_values for batch updates
 * SAFE: Auto-detects available columns and skips non-existent ones
 */
export async function updateLead(
  core: MondayCore,
  itemId: string,
  updates: Partial<Omit<Lead, 'id'>>
): Promise<void> {
  const config = core.getConfig();
  const cm = config.columnMappings?.leads ?? DEFAULT_LEAD_COLUMN_MAPPING;

  // Get available columns from the board to avoid "column doesn't exist" errors
  let availableColumns: Set<string> | null = null;
  try {
    const board = await core.getBoard(config.leadsBoardId);
    if (board?.columns) {
      availableColumns = getAvailableColumnIds(board.columns);
      logger.log('[Monday] Available lead columns:', Array.from(availableColumns));
    }
  } catch (err) {
    logger.simpleWarn('[Monday] Could not fetch board columns, will attempt all updates:', err);
  }

  // Helper to safely get column ID
  const col = (key: string, fallback: string) => cm[key] ?? fallback;

  // Helper to check if column exists on board
  const columnExists = (columnId: string): boolean => {
    if (!availableColumns) return true; // If we couldn't fetch columns, try anyway
    return availableColumns.has(columnId);
  };

  const columnValues: Record<string, unknown> = {};
  const skippedColumns: string[] = [];

  // Map updates to column values (only if column exists)
  if (updates.contactName !== undefined) {
    const colId = col('contactName', 'contact_name');
    if (columnExists(colId)) {
      columnValues[colId] = updates.contactName;
    } else {
      skippedColumns.push(`contactName (${colId})`);
    }
  }
  if (updates.contactEmail !== undefined) {
    const colId = col('contactEmail', 'email');
    if (columnExists(colId)) {
      columnValues[colId] = {
        email: updates.contactEmail,
        text: updates.contactEmail,
      };
    } else {
      skippedColumns.push(`contactEmail (${colId})`);
    }
  }
  // Skip phone field - Monday's API is too strict about format
  if (updates.contactPhone !== undefined) {
    logger.log(`[Monday] Skipping phone field update: ${updates.contactPhone}`);
    skippedColumns.push('contactPhone (skipped - API format issues)');
  }
  if (updates.stage !== undefined) {
    const colId = col('stage', 'status');
    if (columnExists(colId)) {
      columnValues[colId] = { label: updates.stage };
    } else {
      skippedColumns.push(`stage (${colId})`);
    }
  }
  if (updates.value !== undefined) {
    const colId = col('value', 'numbers');
    if (columnExists(colId)) {
      columnValues[colId] = String(updates.value);
    } else {
      skippedColumns.push(`value (${colId})`);
    }
  }
  // Set enriched status and enrichment date when apolloEnriched is set
  if (updates.apolloEnriched !== undefined) {
    // Try "Enriched" column first, then fallback to "checkbox"
    const enrichedCol = col('apolloEnriched', 'enriched');
    if (columnExists(enrichedCol)) {
      columnValues[enrichedCol] = { checked: updates.apolloEnriched ? 'true' : 'false' };
    } else {
      // Try alternative column names
      const altCol = col('enriched', 'checkbox');
      if (columnExists(altCol)) {
        columnValues[altCol] = { checked: updates.apolloEnriched ? 'true' : 'false' };
      }
    }

    // Set enrichment date if enriching
    if (updates.apolloEnriched) {
      const enrichmentDateCol = col('enrichmentDate', 'enrichment_date');
      if (columnExists(enrichmentDateCol)) {
        columnValues[enrichmentDateCol] = { date: new Date().toISOString().split('T')[0] };
      } else {
        // Try alternative column name
        const altDateCol = col('enrichment_date', 'date');
        if (columnExists(altDateCol)) {
          columnValues[altDateCol] = { date: new Date().toISOString().split('T')[0] };
        }
      }
    }
  }
  if (updates.notes !== undefined) {
    const colId = col('notes', 'long_text');
    if (columnExists(colId)) {
      columnValues[colId] = { text: updates.notes };
    } else {
      skippedColumns.push(`notes (${colId})`);
    }
  }

  if (skippedColumns.length > 0) {
    logger.log('[Monday] Skipped non-existent columns:', skippedColumns.join(', '));
  }

  if (Object.keys(columnValues).length === 0) {
    logger.log('[Monday] No valid columns to update for lead', itemId);
    return; // Nothing to update
  }

  logger.log('[Monday] Updating lead columns:', Object.keys(columnValues));
  await mondayClient.patch<{ id: string }>(
    `/hub/items/${itemId}/columns`,
    {
      boardId: config.leadsBoardId,
      itemId,
      columnValues,
    },
  );
  mondayClient.invalidateCache('/hub/leads');

  // Invalidate cache
  core.invalidateLeadsCache();
}

/**
 * Update the linked Account for a lead with enrichment data
 * SMART: Auto-detects accounts board from board relation, maps enrichment fields by column title
 */
export async function updateLinkedAccount(
  core: MondayCore,
  leadId: string,
  accountData: {
    industry?: string;
    size?: string;
    location?: string;
    website?: string;
    linkedin?: string;
    technologies?: string[];
  },
  leadContext?: {
    company?: string;
    revenue?: string;
    value?: number;
    stage?: string;
    nextAction?: string;
  }
): Promise<void> {
  const config = core.getConfig();
  let accountsBoardId = config.accountsBoardId;
  let accountsRelationColumnId: string | null = null;

  // Auto-detect accounts board from leads board relation column if not configured
  // Also store the column ID so we use the SAME column for finding linked items
  if (!accountsBoardId) {
    try {
      const leadsBoard = await core.getBoard(config.leadsBoardId);
      if (leadsBoard?.columns) {
        // Find board relation column that links to accounts
        // PRIORITY: First look for columns with "account" in title, then fall back to generic IDs
        const relationColumn = leadsBoard.columns.find(col =>
          col.type === 'board_relation' &&
          (col.title.toLowerCase().includes('account'))
        ) || leadsBoard.columns.find(col =>
          col.type === 'board_relation' &&
          (col.title.toLowerCase().includes('company') ||
           col.id === 'connect_boards2' ||
           col.id === 'connect_boards')
        );

        if (relationColumn?.settings_str) {
          try {
            const settings = JSON.parse(relationColumn.settings_str);
            // boardIds is an array of linked board IDs
            accountsBoardId = settings.boardIds?.[0]?.toString();
            if (accountsBoardId) {
              accountsRelationColumnId = relationColumn.id;
              logger.log('[Monday] Auto-detected accounts board:', accountsBoardId, 'from column:', relationColumn.title, '(id:', relationColumn.id, ')');
            }
          } catch {
            logger.simpleWarn('[Monday] Could not parse board relation settings');
          }
        }
      }
    } catch (err) {
      logger.simpleWarn('[Monday] Could not auto-detect accounts board:', err);
    }
  }

  if (!accountsBoardId) {
    logger.log('[Monday] No accounts board configured or detected, skipping account update');
    return;
  }

  // If accountsBoardId was configured but we haven't detected the relation column yet,
  // find the column that links to the configured accounts board
  if (!accountsRelationColumnId) {
    try {
      const leadsBoard = await core.getBoard(config.leadsBoardId);
      if (leadsBoard?.columns) {
        // Find the board relation column that links to our accounts board
        const relationColumn = leadsBoard.columns.find(col => {
          if (col.type !== 'board_relation' || !col.settings_str) return false;
          try {
            const settings = JSON.parse(col.settings_str);
            return settings.boardIds?.includes(parseInt(accountsBoardId!)) ||
                   settings.boardIds?.includes(accountsBoardId);
          } catch {
            return false;
          }
        });
        if (relationColumn) {
          accountsRelationColumnId = relationColumn.id;
          logger.log('[Monday] Found relation column linking to accounts board:', relationColumn.title, '(id:', relationColumn.id, ')');
        }
      }
    } catch (err) {
      logger.simpleWarn('[Monday] Could not find accounts relation column:', err);
    }
  }

  // First, get the lead to find the linked account
  const hubItem = await mondayClient.get<HubBoardItem>(`/hub/item/${leadId}`);
  const lead = { items: hubItem ? [hubItemToMondayItem(hubItem)] : [] };

  const leadItem = lead.items[0];
  if (!leadItem) {
    logger.simpleWarn('[Monday] Lead not found:', leadId);
    return;
  }

  // Find the account link column (board relation)
  // IMPORTANT: Use the same column that was used to detect the accounts board
  // to avoid picking up linked items from a different board relation column
  const accountLinkColumn = leadItem.column_values.find((cv) => {
    // If we detected a specific accounts relation column, only use that one
    if (accountsRelationColumnId) {
      return cv.id === accountsRelationColumnId;
    }
    // Fallback: Check by common IDs (only if no specific column was detected)
    if (['connect_boards2', 'connect_boards', 'account', 'company', 'board_relation'].includes(cv.id)) {
      return true;
    }
    // Check if it has linked items (board relation columns have this structure)
    if (cv.value) {
      try {
        const parsed = JSON.parse(cv.value);
        return parsed?.linkedPulseIds || parsed?.linked_item_ids;
      } catch {
        return false;
      }
    }
    return false;
  });

  // Parse the linked item IDs from the board relation value
  let linkedAccountId: string | null = null;
  if (accountLinkColumn?.value) {
    try {
      const parsed = JSON.parse(accountLinkColumn.value);
      linkedAccountId = parsed?.linkedPulseIds?.[0]?.linkedPulseId?.toString() ||
                        parsed?.linked_item_ids?.[0]?.toString() ||
                        null;
      logger.log('[Monday] Found linked account ID:', linkedAccountId, 'from column:', accountLinkColumn.id);
    } catch {
      logger.simpleWarn('[Monday] Could not parse account link column value:', accountLinkColumn.value);
    }
  }

  if (!linkedAccountId) {
    logger.log('[Monday] No linked account found for lead', leadId);
    return;
  }

  // Get board columns with full details (id, title, type, settings_str for dropdowns)
  let boardColumns: Array<{ id: string; title: string; type: string; settings_str?: string }> = [];
  try {
    const board = await core.getBoard(accountsBoardId);
    if (board?.columns) {
      boardColumns = board.columns;
      logger.log('[Monday] Account board columns:', boardColumns.map(c => `${c.title} (${c.id}, ${c.type})`));
      // Log dropdown columns specifically to debug settings
      const dropdownColumns = boardColumns.filter(c => c.type === 'dropdown');
      logger.log('[Monday] Dropdown columns found:', dropdownColumns.length);
      dropdownColumns.forEach(c => {
        logger.log(`[Monday] Dropdown "${c.title}": settings_str exists=${!!c.settings_str}, sample=${c.settings_str?.slice(0, 100)}`);
      });
    }
  } catch (err) {
    logger.simpleWarn('[Monday] Could not fetch accounts board columns:', err);
    return;
  }

  // Helper to find column by title patterns (case insensitive)
  // Prefers text-type columns over special types like 'location'
  const findColumnByTitle = (patterns: string[]): { id: string; type: string; settings_str?: string } | null => {
    for (const pattern of patterns) {
      // Find all columns matching this pattern
      const matchingCols = boardColumns.filter(c => c.title.toLowerCase().includes(pattern.toLowerCase()));
      if (matchingCols.length > 0) {
        // Prefer text-type columns over special types (location, etc.)
        const textCol = matchingCols.find(c => c.type === 'text');
        if (textCol) {
          logger.log(`[Monday] Found text column for pattern "${pattern}": ${textCol.id} (${textCol.title})`);
          return { id: textCol.id, type: textCol.type, settings_str: textCol.settings_str };
        }
        // Also accept long-text columns
        const longTextCol = matchingCols.find(c => c.type === 'long-text' || c.type === 'long_text');
        if (longTextCol) {
          logger.log(`[Monday] Found long-text column for pattern "${pattern}": ${longTextCol.id} (${longTextCol.title})`);
          return { id: longTextCol.id, type: longTextCol.type, settings_str: longTextCol.settings_str };
        }
        // Fall back to first match if no text column found
        const col = matchingCols[0]!;
        logger.log(`[Monday] Found column for pattern "${pattern}": ${col.id} (${col.title}, type: ${col.type})`);
        return { id: col.id, type: col.type, settings_str: col.settings_str };
      }
    }
    return null;
  };

  // Helper to find the correctly-cased dropdown label
  const findDropdownLabel = (settingsStr: string | undefined, value: string): string | null => {
    logger.log(`[Monday] findDropdownLabel called with value="${value}", settingsStr exists=${!!settingsStr}`);
    if (!settingsStr) {
      logger.log('[Monday] No settings_str, returning original value');
      return value; // Fallback to original value
    }
    try {
      const settings = JSON.parse(settingsStr);
      logger.log('[Monday] Parsed dropdown settings:', JSON.stringify(settings).slice(0, 300));
      // Monday.com dropdown settings format:
      // { labels: [{ name: "Tech", id: 1 }, { name: "Construction", id: 15 }, ...] }
      const labels = settings.labels;
      if (!labels) {
        logger.log('[Monday] No labels in settings, returning original value');
        return value;
      }

      // Handle array of label objects (Monday.com format): [{ name: "Tech", id: 1 }, ...]
      if (Array.isArray(labels)) {
        for (const label of labels) {
          // Label can be an object with name property or a simple string
          const labelName = typeof label === 'string' ? label : label?.name;
          if (labelName && labelName.toLowerCase() === value.toLowerCase()) {
            logger.log(`[Monday] Found dropdown label match: "${value}" → "${labelName}"`);
            return labelName;
          }
        }
      }
      // Handle object with index keys (legacy format): { "1": { name: "Tech" }, ... }
      else if (typeof labels === 'object') {
        for (const key of Object.keys(labels)) {
          const labelObj = labels[key];
          const labelName = typeof labelObj === 'string' ? labelObj : labelObj?.name;
          if (labelName && labelName.toLowerCase() === value.toLowerCase()) {
            logger.log(`[Monday] Found dropdown label match: "${value}" → "${labelName}"`);
            return labelName;
          }
        }
      }

      logger.simpleWarn(`[Monday] No dropdown label match found for "${value}", available labels:`, labels.map((l: { name?: string }) => l?.name || l));
      return null; // No match found, skip this field to avoid API error
    } catch (err) {
      logger.simpleWarn('[Monday] Could not parse dropdown settings:', err);
      return value;
    }
  };

  // Build column values for account update
  const columnValues: Record<string, unknown> = {};
  const updatedFields: string[] = [];
  const skippedFields: string[] = [];

  // Build AI Analysis for description field (same format as UI)
  let aiAnalysis: string | undefined;
  if (leadContext?.company && (accountData.industry || accountData.size)) {
    const company = leadContext.company;
    const size = accountData.size || 'growing';
    const industry = accountData.industry || 'technology';
    const revenueText = leadContext.revenue ? ` With ${leadContext.revenue} in revenue,` : '';
    const valueText = leadContext.value ? `$${leadContext.value.toLocaleString()}` : '$0';
    const stage = leadContext.stage || 'New';
    const nextAction = leadContext.nextAction || '';

    aiAnalysis = `${company} is a ${size} company in the ${industry} sector.${revenueText} This represents a strong opportunity with a deal value of ${valueText}. Current stage: ${stage}. Next step: ${nextAction}.`;
  }

  // Field mappings: enrichment field -> [possible column title patterns]
  const fieldMappings: Record<string, { patterns: string[]; value: string | undefined }> = {
    website: {
      patterns: ['domain', 'website', 'url', 'web', 'site'],
      value: accountData.website,
    },
    industry: {
      patterns: ['industry', 'sector', 'vertical'],
      value: accountData.industry,
    },
    size: {
      patterns: ['size', 'employees', 'company size', 'headcount', 'employee count'],
      value: accountData.size,
    },
    location: {
      patterns: ['location', 'address', 'headquarters', 'hq', 'city', 'region'],
      value: accountData.location,
    },
    linkedin: {
      patterns: ['linkedin', 'social', 'linked in'],
      value: accountData.linkedin,
    },
    description: {
      patterns: ['description', 'notes', 'about', 'summary', 'overview'],
      value: aiAnalysis,
    },
    techStack: {
      patterns: ['tech stack', 'technologies', 'tech', 'stack', 'technology'],
      value: accountData.technologies?.join(', '),
    },
  };

  // Try to map each enrichment field to an Account column
  logger.log('[Monday] Field mapping values:', Object.entries(fieldMappings).map(([k, v]) => `${k}=${v.value ? 'set' : 'empty'}`).join(', '));
  for (const [field, { patterns, value }] of Object.entries(fieldMappings)) {
    if (!value) {
      logger.log(`[Monday] Skipping field "${field}" - no value`);
      continue;
    }

    const column = findColumnByTitle(patterns);
    if (column) {
      // Format value based on column type
      if (column.type === 'link') {
        columnValues[column.id] = { url: value, text: value };
        updatedFields.push(`${field} → ${column.id}`);
      } else if (column.type === 'dropdown') {
        // Find the correctly-cased dropdown label to avoid API errors
        logger.log(`[Monday] Processing dropdown field "${field}" with value "${value}", column.settings_str exists=${!!column.settings_str}`);
        const correctLabel = findDropdownLabel(column.settings_str, value);
        logger.log(`[Monday] Dropdown result: correctLabel="${correctLabel}"`);
        if (correctLabel) {
          columnValues[column.id] = { labels: [correctLabel] };
          updatedFields.push(`${field} → ${column.id} (label: ${correctLabel})`);
        } else {
          skippedFields.push(`${field} (no matching dropdown label for "${value}")`);
        }
      } else if (column.type === 'long-text' || column.type === 'long_text') {
        columnValues[column.id] = { text: value };
        updatedFields.push(`${field} → ${column.id}`);
      } else if (column.type === 'location') {
        // Monday location columns require lat/lng - skip and log
        // User needs to change column type to "Text" in Monday board settings
        logger.simpleWarn(`[Monday] Skipping location column "${column.id}" - requires lat/lng. Change column type to Text in Monday.`);
        skippedFields.push(`${field} (location column still requires lat/lng - verify column type is Text in Monday)`);
      } else {
        // text, numbers, etc.
        columnValues[column.id] = value;
        updatedFields.push(`${field} → ${column.id}`);
      }
    } else {
      skippedFields.push(field);
    }
  }

  if (updatedFields.length > 0) {
    logger.log('[Monday] Mapped enrichment fields:', updatedFields.join(', '));
  }
  if (skippedFields.length > 0) {
    logger.log('[Monday] Skipped (no matching column):', skippedFields.join(', '));
  }

  // Add Enriched and Enrichment Date columns for accounts
  // Match "Enriched", "Enriched?", or columns starting with "enriched"
  const enrichedColumn = boardColumns.find(
    (col) => col.title.toLowerCase().startsWith('enriched') ||
             col.id === 'enriched' ||
             col.id.startsWith('boolean_') && col.title.toLowerCase().includes('enriched')
  );
  if (enrichedColumn) {
    columnValues[enrichedColumn.id] = { checked: 'true' };
    logger.log(`[Monday] Setting Account Enriched column: ${enrichedColumn.id}`);
  } else {
    logger.log('[Monday] Enriched column not found on accounts board');
  }

  const enrichmentDateColumn = boardColumns.find(
    (col) => col.title.toLowerCase() === 'enrichment date' || col.id === 'enrichment_date'
  );
  if (enrichmentDateColumn) {
    columnValues[enrichmentDateColumn.id] = { date: new Date().toISOString().split('T')[0] };
    logger.log(`[Monday] Setting Account Enrichment Date column: ${enrichmentDateColumn.id}`);
  } else {
    logger.log('[Monday] Enrichment Date column not found on accounts board');
  }

  if (Object.keys(columnValues).length === 0) {
    logger.log('[Monday] No valid columns to update for account', linkedAccountId);
    return;
  }

  logger.log('[Monday] Updating account', linkedAccountId, 'with:', Object.keys(columnValues));
  logger.log('[Monday] Column values:', JSON.stringify(columnValues, null, 2));
  try {
    await mondayClient.patch<{ id: string }>(
      `/hub/items/${linkedAccountId}/columns`,
      {
        boardId: accountsBoardId,
        itemId: linkedAccountId,
        columnValues: columnValues,
      },
    );
  } catch (error) {
    // Check if the account was deleted (ResourceNotFoundException)
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (errorMessage.includes('ResourceNotFoundException') || errorMessage.includes('Item not found')) {
      logger.simpleWarn(`[Monday] Linked account ${linkedAccountId} no longer exists (may have been deleted). Skipping update.`);
      return; // Don't throw - gracefully skip
    }

    logger.simpleError('[Monday] Failed to update account columns. Trying one by one...');
    // Try updating columns one by one to identify the problematic one
    for (const [columnId, value] of Object.entries(columnValues)) {
      try {
        await mondayClient.patch<{ id: string }>(
          `/hub/items/${linkedAccountId}/columns`,
          {
            boardId: accountsBoardId,
            itemId: linkedAccountId,
            columnValues: { [columnId]: value },
          },
        );
        logger.log(`[Monday] Successfully updated column: ${columnId}`);
      } catch (colError) {
        // Check if account not found during column-by-column update
        const colErrorMessage = colError instanceof Error ? colError.message : String(colError);
        if (colErrorMessage.includes('ResourceNotFoundException') || colErrorMessage.includes('Item not found')) {
          logger.simpleWarn(`[Monday] Linked account ${linkedAccountId} no longer exists. Skipping remaining updates.`);
          core.invalidateAccountsCache();
          return; // Don't throw - gracefully skip
        }
        logger.simpleError(`[Monday] Failed to update column ${columnId}:`, colError);
      }
    }
    // Only throw if it wasn't a "not found" error
    throw error;
  }

  logger.log('[Monday] Successfully updated account', linkedAccountId);

  // Invalidate cache
  core.invalidateAccountsCache();
}

/**
 * Create an update (comment/activity) on an item
 * This appears in the item's Updates/Activity section
 */
export async function createUpdate(
  _core: MondayCore,
  itemId: string,
  body: string
): Promise<string | null> {
  try {
    const data = await mondayClient.post<{ id: string; body: string }>(
      `/hub/items/${itemId}/updates`,
      { itemId, body: body },
    );
    logger.log('[Monday] Created update on item', itemId);
    return data?.id ?? null;
  } catch (err) {
    logger.simpleError('[Monday] Failed to create update:', err);
    return null;
  }
}

/**
 * Create an enrichment update on a lead item
 * Formats Apollo enrichment data as a nicely formatted update
 */
export async function createEnrichmentUpdate(
  core: MondayCore,
  itemId: string,
  enrichmentData: {
    industry?: string;
    size?: string;
    location?: string;
    website?: string;
    linkedin?: string;
    phone?: string;
    founded?: number;
    revenue?: string;
  },
  source: string = 'Apollo'
): Promise<string | null> {
  const lines: string[] = [
    `🔍 **${source} Enrichment Data**`,
    '',
  ];

  if (enrichmentData.industry) {
    lines.push(`**Industry:** ${enrichmentData.industry}`);
  }
  if (enrichmentData.size) {
    lines.push(`**Company Size:** ${enrichmentData.size}`);
  }
  if (enrichmentData.location) {
    lines.push(`**Location:** ${enrichmentData.location}`);
  }
  if (enrichmentData.website) {
    lines.push(`**Website:** ${enrichmentData.website}`);
  }
  if (enrichmentData.linkedin) {
    lines.push(`**LinkedIn:** ${enrichmentData.linkedin}`);
  }
  if (enrichmentData.phone) {
    lines.push(`**Phone:** ${enrichmentData.phone}`);
  }
  if (enrichmentData.founded) {
    lines.push(`**Founded:** ${enrichmentData.founded}`);
  }
  if (enrichmentData.revenue) {
    lines.push(`**Revenue:** ${enrichmentData.revenue}`);
  }

  lines.push('');
  lines.push(`_Enriched via Marketing Intelligence Hub on ${new Date().toLocaleDateString()}_`);

  const body = lines.join('\n');
  return createUpdate(core, itemId, body);
}

/**
 * Delete a lead
 */
export async function deleteLead(core: MondayCore, itemId: string): Promise<void> {
  await mondayClient.delete(`/hub/items/${itemId}`);
  mondayClient.invalidateCache('/hub/leads');
  core.invalidateLeadsCache();
}

/**
 * Batch create leads (with deduplication)
 * EFFICIENT: Checks for existing leads before creating
 * @param leads - Array of leads to create
 * @param options - Options for batch creation
 * @param options.skipDuplicates - Skip leads with duplicate emails (default: true)
 * @param options.groupId - Specific group ID to use
 * @param options.listName - List name to create/find a group for (takes precedence over groupId)
 */
export async function batchCreateLeads(
  core: MondayCore,
  leads: Array<Omit<Lead, 'id' | 'createdAt' | 'lastActivity'>>,
  options: { skipDuplicates?: boolean; groupId?: string; listName?: string } = {}
): Promise<{ created: string[]; skipped: string[] }> {
  const { skipDuplicates = true, groupId, listName } = options;
  const created: string[] = [];
  const skipped: string[] = [];

  for (const lead of leads) {
    // Check for duplicates by email
    if (skipDuplicates && lead.contactEmail) {
      const existing = await findLeadByEmail(core, lead.contactEmail);
      if (existing) {
        logger.log(`[Monday] Skipping duplicate: ${lead.contactEmail}`);
        skipped.push(lead.contactEmail);
        continue;
      }
    }

    const id = await createLead(core, lead, { groupId, listName });
    created.push(id);
  }

  return { created, skipped };
}
